import type { Vehicle } from '../services/vehicle.service'

/** Empty is unknown, never zero. Database decimals may be returned as strings. */
export function optionalVehicleNumber(value: unknown): number | null {
  if (value == null || (typeof value === 'string' && !value.trim())) return null
  if (typeof value !== 'number' && typeof value !== 'string') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

export const VEHICLE_VOLUME_FRACTION_DIGITS = 2

/** Canonical m³ precision, shared by calculations, persistence and comparisons. */
export function roundVehicleVolume(value: unknown): number | null {
  const volume = optionalVehicleNumber(value)
  if (volume == null) return null
  const scale = 10 ** VEHICLE_VOLUME_FRACTION_DIGITS
  // Compensate for binary floating-point noise at decimal half-way values (e.g. 10.075).
  return Math.round((volume + Number.EPSILON * Math.max(1, Math.abs(volume))) * scale) / scale
}

export function vehicleInteriorVolume(length: unknown, width: unknown, height: unknown): number | null {
  const dimensions = [length, width, height].map(optionalVehicleNumber)
  if (dimensions.some(value => value == null || value <= 0)) return null
  return roundVehicleVolume(dimensions.reduce<number>((volume, value) => volume * value!, 1) / 1_000_000_000)
}

/** Compare the same two-decimal values shown and persisted by the fleet form. */
export function hasVehicleVolumeDifference(calculated: unknown, declared: unknown): boolean {
  const stored = roundVehicleVolume(declared)
  if (stored == null) return false
  const volume = roundVehicleVolume(calculated)
  return volume == null || volume !== stored
}

export function vehiclePayload(gross: unknown, tare: unknown): number | null {
  const grossMass = optionalVehicleNumber(gross)
  const tareMass = optionalVehicleNumber(tare)
  if (grossMass == null || tareMass == null || tareMass < 0 || grossMass < tareMass) return null
  return grossMass - tareMass
}

export function isFleetTractor(vehicle: Partial<Vehicle> & { isTractor?: boolean }): boolean {
  const config = vehicle.axle_configuration
  return vehicle.isTractor === true || vehicle.vehicle_subtype?.startsWith('tracteur_') === true ||
    optionalVehicleNumber(vehicle.max_combination_weight) != null ||
    (vehicle.type === 'TRUCK' && !!config && (config.fifthWheelPosition != null || config.fifthWheelMaxLoad != null))
}

const numericFields = [
  'year', 'capacity_volume', 'capacity_weight', 'empty_weight', 'length', 'width', 'height',
  'tail_lift_capacity', 'max_weight_permitted', 'max_combination_weight', 'total_mileage',
  'fuel_consumption_average', 'maintenance_cost_per_km', 'breakdown_rate', 'availability_rate',
  'average_rating', 'max_driving_distance_per_day', 'hourly_cost', 'cost_per_km', 'daily_fixed_cost',
  'max_floor_pressure', 'pressure_cell_target_size', 'target_cg_x', 'target_cg_y', 'max_cg_deviation',
  'favorite_rating',
] as const

/** Preserve explicit nulls so PATCH semantics can clear an optional value. */
export function prepareVehicleFormPayload(vehicle: Partial<Vehicle>): Partial<Vehicle> {
  const payload = { ...vehicle } as Record<string, unknown>
  for (const field of ['fifth_wheel', 'constraintEngineConfig', 'isTractor', 'max_weight', 'dimensions']) delete payload[field]
  for (const field of ['fuel_type', 'euro_emission_standard']) {
    if (payload[field] == null || payload[field] === '') delete payload[field]
  }
  for (const field of numericFields) {
    const value = payload[field]
    if (value === undefined) { delete payload[field]; continue }
    const number = optionalVehicleNumber(value)
    if (number == null && value != null && !(typeof value === 'string' && !value.trim())) {
      throw new Error(`Valeur numérique invalide : ${field}. Corrigez-la avant de sauvegarder.`)
    }
    payload[field] = field === 'capacity_volume' ? roundVehicleVolume(number) : number
  }
  const axleConfig = vehicle.axle_configuration
  if (axleConfig && typeof axleConfig === 'object' && vehicle.type !== 'CONTAINER' && axleConfig.physicsType !== 'container') {
    payload.axle_configuration = { ...axleConfig, enforceAxleLimits: axleConfig.enforceAxleLimits ?? true }
  }
  return payload as Partial<Vehicle>
}
